import { useQueries, useQuery } from '@tanstack/react-query';
import type { Recommendation } from '@lizrd/contracts';
import { getOrgSavingsSummary, getRecommendations } from '../../lib/api';

export interface EnvRecs {
  domainId: string;
  name: string;
  recs: Recommendation[];
}

/**
 * Recommendations for every domain the user can read, keyed by domain.
 * Powers the org-level realized-savings views (trend, shout-outs). React Query
 * dedupes the per-domain `['recommendations', id]` queries with the ones the
 * rest of the app already uses. Returns `loaded` once every domain resolves.
 */
export function useOrgDomainRecs(): { envs: EnvRecs[]; loaded: boolean } {
  const summary = useQuery({ queryKey: ['org-savings-summary'], queryFn: getOrgSavingsSummary });
  const list = summary.data?.domains ?? [];
  const queries = useQueries({
    queries: list.map((e) => ({
      queryKey: ['recommendations', e.domainId],
      queryFn: () => getRecommendations(e.domainId),
      refetchInterval: 60_000,
    })),
  });

  const envs = list.map((e, i) => ({
    domainId: e.domainId,
    name: e.name,
    recs: queries[i]?.data?.items ?? [],
  }));
  const loaded = !!summary.data && queries.every((q) => q.data !== undefined);
  return { envs, loaded };
}
